import { RoutesInterface } from '@navigation/authRoutes';
import { SidebarGroup } from '@navigation/sidebarGroups';

// Iconos
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import StoreIcon from '@mui/icons-material/Store';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import PeopleIcon from '@mui/icons-material/People';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import DescriptionIcon from '@mui/icons-material/Description';
import DinnerDining from '@mui/icons-material/DinnerDining';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import LoyaltyIcon from '@mui/icons-material/Loyalty';

// Pantallas
import MyProfileScreen from '@screens/MyProfile/MyProfileScreen';
import DashboardScreen from '@screens/Dashboard/DashboardScreen';
import SalesReportScreen from '@screens/SalesReport/CollaboratorSalesReportScreen';
import POSScreen from '@screens/POS/POSScreen';
import ListOrdersScreen from '@screens/Orders/ListOrders';
import ListTablesKiosksScreen from '@screens/TableKiosks/ListTablesKiosksScreen';
import ListCustomersScreen from '@screens/Customers/ListCustomersScreen';
import RewardsPromotionsScreen from '@screens/Rewards/RewardsPromotionsScreen';
import NotificationsScreen from '@screens/Notifications/NotificationsScreen';
import CatalogScreen from '@screens/Catalog/CatalogScreen';
import SalesChannelsScreen from '@screens/SalesChannels/SalesChannelsScreen';
import ListUsersScreen from '@screens/Users/ListUsers';
import ListBranchOfficesScreen from '@screens/BranchOffices/ListBranchOfficesScreen';
import ListBranchOfficeInventoryScreen from '@screens/BranchOffices/Inventory/InventoryScreen';
import InventoryPOS from '@screens/BranchOffices/Inventory/InventoryPOS';
import CedisScreen from '@screens/Cedis/CedisScreen';
import InventoryScreen from '@screens/Cedis/Inventory/InventoryScreen';
import ListInventoryOrdersScreen from '@screens/Cedis/Inventory/ListInventoryOrdersScreen';
import InventoryAddInputs from '@screens/Cedis/Inventory/InventoryAddInputs';
import InventoryRemoveInputs from '@screens/Cedis/Inventory/InventoryRemoveInputs';
import InventoryHistoryDetails from '@screens/Cedis/Inventory/InventoryInputDetails';

// ============================================================================
// RUTAS DEL ADMIN — el admin ve TODO el sistema sin cambiar de perfil
// (decisión de Irving, 2026-08-18).
//
// Antes el admin NO tenía `orders` ni `tablesKiosks`: para ver el tablero de
// cocina o las mesas TENÍA que cambiarse a gerente/colaborador, y al hacerlo
// perdía sus permisos de admin sin enterarse (`hasPermission` lee
// `profileSelected`). Ahora el cambio de perfil es SOLO para previsualizar
// ("Ver como"), no para trabajar.
//
// `home` es la landing (AppRouter redirige `/{perfil}` → `home`): para el admin
// es el Dashboard. `dashboard` se conserva como alias OCULTO para no romper
// links viejos.
// ============================================================================

const adminRoutes: RoutesInterface[] = [
    // ─── Sueltas (fuera de grupo, arriba del todo) ───────────────────────────
    {
        path: 'myProfile',
        name: 'Mi Perfil',
        element: MyProfileScreen
    },
    {
        path: 'home',
        name: 'Dashboard',
        element: DashboardScreen,
        icon: DashboardIcon
    },
    {
        // Alias oculto de la landing: `/admin/dashboard` siguió existiendo
        // durante años, hay quien lo tiene en favoritos
        path: 'dashboard',
        name: 'Dashboard',
        element: DashboardScreen
    },

    // ─── Operación ───────────────────────────────────────────────────────────
    {
        path: 'pos',
        name: 'POS',
        element: POSScreen,
        icon: PointOfSaleOutlinedIcon,
        group: SidebarGroup.Operation
    },
    {
        path: 'orders',
        name: 'Pedidos',
        element: ListOrdersScreen,
        icon: DescriptionIcon,
        group: SidebarGroup.Operation
    },
    {
        path: 'tablesKiosks',
        name: 'Mesas y Kioscos',
        element: ListTablesKiosksScreen,
        icon: DinnerDining,
        group: SidebarGroup.Operation
    },
    {
        path: 'customers',
        name: 'Clientes',
        element: ListCustomersScreen,
        icon: PeopleIcon,
        group: SidebarGroup.Operation
    },

    // ─── Análisis ────────────────────────────────────────────────────────────
    {
        path: 'salesReport',
        name: 'Informe de ventas',
        element: SalesReportScreen,
        icon: QueryStatsIcon,
        group: SidebarGroup.Analytics
    },

    // ─── Catálogo y precios ──────────────────────────────────────────────────
    {
        path: 'catalog',
        name: 'Catálogo',
        element: CatalogScreen,
        icon: MenuBookIcon,
        group: SidebarGroup.Catalog
    },
    {
        // Se llamaba "Punto de venta", igual que el POS del colaborador: dos
        // cosas distintas con el mismo nombre. Son los CANALES de venta
        path: 'salesChannels',
        name: 'Canales de venta',
        element: SalesChannelsScreen,
        icon: PointOfSaleIcon,
        group: SidebarGroup.Catalog
    },
    {
        // Centro de mensajes: avisos internos + los WhatsApp que salen del
        // sistema. Van juntos porque son dos canales con reglas distintas y la
        // decisión de por cuál mandar algo se toma comparándolos.
        path: 'notifications',
        name: 'Notificaciones',
        element: NotificationsScreen,
        icon: NotificationsNoneIcon,
        group: SidebarGroup.Settings
    },
    {
        // Recompensas y códigos promocionales viven juntos desde el 2026-08-29
        // (pedido de Irving): son la misma decisión de negocio, cómo se premia
        // al cliente, y eran dos renglones separados del sidebar.
        path: 'customers/rewards',
        name: 'Promociones',
        element: RewardsPromotionsScreen,
        icon: LoyaltyIcon,
        group: SidebarGroup.Catalog
    },
    {
        // Alias OCULTO (sin `icon`, no sale en el sidebar): la pantalla de
        // códigos tenía su propia ruta y un bookmark viejo no debe caer en nada.
        path: 'customers/promotionalCodes',
        name: 'Códigos promocionales',
        element: RewardsPromotionsScreen
    },

    // ─── Inventario ──────────────────────────────────────────────────────────
    {
        path: 'branchOffices/inventory',
        name: 'Inventario de sucursal',
        element: ListBranchOfficeInventoryScreen,
        icon: Inventory2Icon,
        group: SidebarGroup.Inventory
    },
    {
        path: 'branchOffices/inventory/pos',
        name: 'Punto de Venta de Inventario',
        element: InventoryPOS
    },
    {
        path: 'branchOffices/inventory/inventoryOrderDetails',
        name: 'Detalles del pedido',
        element: InventoryPOS
    },
    {
        path: 'distributionCenters/inventory',
        name: 'Inventario CEDIS',
        element: InventoryScreen,
        icon: WarehouseIcon,
        group: SidebarGroup.Inventory
    },
    {
        path: 'distributionCenters/orders',
        name: 'Pedidos de insumos',
        element: ListInventoryOrdersScreen,
        icon: LocalShippingIcon,
        group: SidebarGroup.Inventory
    },
    {
        path: 'distributionCenters/inventory/addInputs',
        name: 'Registrar entradas',
        element: InventoryAddInputs
    },
    {
        path: 'distributionCenters/inventory/removeInputs',
        name: 'Registrar salidas',
        element: InventoryRemoveInputs
    },
    {
        path: 'distributionCenters/inventory/inventoryHistoryDetails',
        name: 'Detalles del movimiento',
        element: InventoryHistoryDetails
    },
    {
        path: 'distributionCenters/orders/details',
        name: 'Detalles del pedido',
        element: InventoryPOS
    },

    // ─── Configuración ───────────────────────────────────────────────────────
    {
        path: 'branchOffices',
        name: 'Sucursales',
        element: ListBranchOfficesScreen,
        icon: StoreIcon,
        group: SidebarGroup.Settings
    },
    {
        path: 'distributionCenters',
        name: 'CEDIS',
        element: CedisScreen,
        icon: ContentPasteIcon,
        group: SidebarGroup.Settings
    },
    {
        // Antes vivía en `home` (era la landing del admin). Se movió a `users`
        // para que la landing sea el Dashboard
        path: 'users',
        name: 'Usuarios',
        element: ListUsersScreen,
        icon: PersonIcon,
        group: SidebarGroup.Settings
    }
];

export default adminRoutes;
